import moment from "moment-timezone";
import "moment/locale/es";

moment.locale("es");

const unwrap = (item) => (item && item.event ? item.event : item) || {};

const getEventStartDate = (item) => {
  const ev = unwrap(item);
  return ev.dateConfiguration && ev.dateConfiguration.startDate
    ? ev.dateConfiguration.startDate
    : null;
};

const getEventEndDate = (item) => {
  const ev = unwrap(item);
  return ev.dateConfiguration && ev.dateConfiguration.endDate
    ? ev.dateConfiguration.endDate
    : null;
};

export const resolveEnrollmentStatus = (item) => {
  if (!item) return { variant: "neutral", label: "Sin estado" };
  const rawStatus = (item.status || "").toUpperCase();
  if (rawStatus === "CANCELLED" || rawStatus === "EXPIRED") {
    return { variant: "error", label: "Cancelado" };
  }
  const start = getEventStartDate(item);
  const end = getEventEndDate(item);
  if (!start) {
    return { variant: "info", label: "Inscrito" };
  }
  const now = moment();
  if (end && moment(end).isBefore(now)) {
    return { variant: "success", label: "Asistido" };
  }
  if (moment(start).isBefore(now) && (!end || moment(end).isSameOrAfter(now))) {
    return { variant: "warning", label: "En vivo" };
  }
  return { variant: "info", label: "Inscrito" };
};

export const isEnrollmentLive = (item) => {
  const start = getEventStartDate(item);
  const end = getEventEndDate(item);
  if (!start) return false;
  const now = moment();
  if (end) {
    return moment(start).isSameOrBefore(now) && moment(end).isSameOrAfter(now);
  }
  return moment(start).isSame(now, "day");
};

export const isEnrollmentPast = (item) => {
  const ref = getEventEndDate(item) || getEventStartDate(item);
  if (!ref) return false;
  return moment(ref).isBefore(moment());
};

export const isEnrollmentUpcoming = (item) => {
  const start = getEventStartDate(item);
  if (!start) return false;
  return moment(start).isAfter(moment());
};

export const hasZoom = (item) => {
  const ev = unwrap(item);
  if (!ev || !ev.zoomConfiguration || !ev.zoomConfiguration.meetingId) return false;
  const modality = (item.ticket && item.ticket.modality) || ev.eventModality;
  return modality === "VIRTUAL" || modality === "HYBRID";
};

export const isPresential = (item) => {
  if (!item || !item.ticket) return false;
  return item.ticket.modality === "PRESENTIAL";
};

export const getEventModality = (item) => {
  if (item && item.ticket && item.ticket.modality) {
    return item.ticket.modality;
  }
  const ev = unwrap(item);
  return ev && ev.eventModality ? ev.eventModality : null;
};

export const getEventCategory = (item) => {
  const ev = unwrap(item);
  return ev && ev.category ? ev.category : null;
};

export const hasDiploma = (item) => {
  if (!item) return false;
  if (item.diplomaUrl) return true;
  if (item.attendeeInformation && item.attendeeInformation.diplomaUrl) return true;
  return isEnrollmentPast(item);
};

export const getDiplomaUrl = (item) => {
  if (!item) return null;
  if (item.diplomaUrl) return item.diplomaUrl;
  if (item.attendeeInformation && item.attendeeInformation.diplomaUrl) {
    return item.attendeeInformation.diplomaUrl;
  }
  return null;
};

export const getZoomJoinUrl = (item) => {
  if (!item) return null;
  if (item.zoomRegistrant && item.zoomRegistrant.joinUrl) return item.zoomRegistrant.joinUrl;
  return null;
};

export const getEventId = (item) => {
  const ev = unwrap(item);
  return (ev && ev._id) || (item && item._id) || null;
};

export const groupEnrollmentsByStatus = (items) => {
  const groups = { upcoming: [], live: [], past: [] };
  (items || []).forEach((item) => {
    if (isEnrollmentLive(item)) {
      groups.live.push(item);
    } else if (isEnrollmentUpcoming(item)) {
      groups.upcoming.push(item);
    } else {
      groups.past.push(item);
    }
  });
  return groups;
};

export const applyEnrollmentFilters = (items, filters = {}) => {
  if (!Array.isArray(items)) return [];
  const search = (filters.search || "").trim().toLowerCase();
  const category = filters.category || null;
  return items.filter((item) => {
    const ev = unwrap(item);
    if (!ev) return false;
    if (search && !(ev.name || "").toLowerCase().includes(search)) return false;
    if (category) {
      const cat = getEventCategory(item);
      if (cat !== category) return false;
    }
    return true;
  });
};

export const extractEnrollmentCategories = (items) => {
  const set = new Set();
  (items || []).forEach((item) => {
    const cat = getEventCategory(item);
    if (cat) set.add(cat);
  });
  return Array.from(set).sort();
};

export const sortEnrollmentsByDate = (items, order = "asc") => {
  const list = [...(items || [])];
  list.sort((a, b) => {
    const sa = getEventStartDate(a);
    const sb = getEventStartDate(b);
    if (!sa && !sb) return 0;
    if (!sa) return 1;
    if (!sb) return -1;
    return order === "asc" ? moment(sa).valueOf() - moment(sb).valueOf() : moment(sb).valueOf() - moment(sa).valueOf();
  });
  return list;
};
